import React from 'react';
import { useNotify } from './NotificationProvider';

/**
 * Global application error boundary.
 * Captures render/runtime errors in descendant React components and shows a fallback UI.
 * Integrates with window.consoleCapture (if present) to allow copying logs.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null, timestamp: null };
  }

  static getDerivedStateFromError(error) {
    return { error, timestamp: Date.now() };
  }

  componentDidCatch(error, errorInfo) {
    // Store last error globally for debugging (avoid circular structures)
    try {
      window.__LAST_ERROR__ = {
        message: error?.message,
        stack: error?.stack,
        componentStack: errorInfo?.componentStack,
        time: new Date().toISOString(),
      };
    } catch (storageError) {
      console.warn('Failed to persist last error snapshot:', storageError);
    }

    // Log with original console to avoid being filtered
    const originalError =
      (window.consoleCapture && window.consoleCapture.originalMethods?.error) || console.error;
    originalError('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ error: null, errorInfo: null, timestamp: null });
  };

  handleCopyLogs = () => {
    if (window.copyAllConsole) {
      window.copyAllConsole(false);
    } else {
      if (this.notify) this.notify('Console capture not initialized', { severity: 'warning' });
    }
  };

  handleCopyErrors = () => {
    if (window.copyAllConsole) {
      window.copyAllConsole(true);
    } else {
      if (this.notify) this.notify('Console capture not initialized', { severity: 'warning' });
    }
  };

  componentDidMount() {
    // Acquire notification hook via temporary functional component bridge
    const self = this;
    function Notifier() {
      const { showMessage } = useNotify();
      React.useEffect(() => {
        self.notify = showMessage;
      }, [showMessage]);
      return null;
    }
    this._notifier = Notifier;
  }

  render() {
    if (this.state.error) {
      const version = window.__HISTORY_FORM_VERSION || 'unknown';
      const Notifier = this._notifier;
      return (
        <div
          style={{
            fontFamily: 'system-ui, Arial, sans-serif',
            padding: '2rem',
            maxWidth: 800,
            margin: '3rem auto',
            border: '1px solid #e0e0e0',
            borderRadius: 12,
            background: '#fff',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          }}
        >
          <h2 style={{ marginTop: 0, color: '#b00020' }}>Something went wrong.</h2>
          {Notifier && <Notifier />}
          <p style={{ lineHeight: 1.4 }}>
            A UI error occurred and was intercepted by the global error boundary. You can try to
            continue by pressing Reset UI. If the error persists, refresh the page (full reload) or
            copy the console output and send it to the dev team.
          </p>
          <details
            style={{
              whiteSpace: 'pre-wrap',
              fontSize: '0.85rem',
              background: '#f9f9f9',
              padding: '0.75rem 1rem',
              borderRadius: 8,
            }}
          >
            <summary style={{ cursor: 'pointer', marginBottom: '0.5rem' }}>Error details</summary>
            {this.state.error?.toString()}\n\n{this.state.error?.stack}\n\nCaptured at:{' '}
            {new Date(this.state.timestamp).toISOString()}\nHistoryForm Version (if relevant):{' '}
            {version}
          </details>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1rem' }}>
            <button onClick={this.handleReset} style={btnStyle}>
              Reset UI
            </button>
            <button onClick={() => window.location.reload()} style={btnStyle}>
              Full Reload
            </button>
            <button onClick={this.handleCopyLogs} style={btnStyle}>
              Copy All Logs
            </button>
            <button onClick={this.handleCopyErrors} style={btnStyle}>
              Copy Errors Only
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const btnStyle = {
  background: '#C9B06E',
  color: '#fff',
  border: 'none',
  padding: '0.6rem 1rem',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: '0.85rem',
  fontWeight: 600,
  letterSpacing: 0.3,
};

export default ErrorBoundary;
